import React, { useState } from 'react';
import ToastActions from 'actions/ToastActions';
import assertions from 'lib/assertions';
import CoachingConstants from 'coaching/CoachingConstants';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import TextWithIcon, { Props as TextWithIconProps } from 'components/TextWithIcon';
import Row from 'components/Row';

export type Props = {
  onSubmitCode: (code: string) => Promise<void>;
  onResendCodeClick: () => void;
  onChangeEmailClick: () => void;
};

function ConfirmEmailForm(props: Props) {
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState<string>();

  const { onSubmitCode, onResendCodeClick, onChangeEmailClick } = props;
  const onContinue = async () => {
    setVerifying(true);
    try {
      await onSubmitCode(code ?? ''); // Should never be undefined due to submit button being disabled
    } catch (err) {
      ToastActions.showErrorToast(`Error verifying your email address.`);
    }
    setVerifying(false);
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        onContinue();
      }}>
      <Row size="small">
        <FormElement
          data-test="confirmation-code-input"
          label="Confirmation code"
          labelHidden
          element={Input}
          validate={e => {
            assertions.number(e.target.value, 'Must be a 4-digit numerical code');
            assertions.lengthEqualTo(e.target.value, 4, 'Must be a 4-digit numerical code');
          }}
          onValidChange={e => setCode(e.target.value)}
          onInvalidChange={() => setCode(undefined)}
          elementProps={{
            autofocus: true,
            placeholder: '0000',
            maxLength: 4,
            size: 'large',
            'data-test': 'confirm-input-box',
          }}
        />
      </Row>
      <Row size="xxsmall">
        Didn't get the code?{' '}
        <OptionsMenu
          onResendCodeClick={onResendCodeClick}
          onChangeEmailClick={onChangeEmailClick}
        />
      </Row>
      <Row size="medium">
        <Buttons axis="y">
          <Button
            disabled={!code || verifying}
            loading={verifying}
            full
            color="koolaid"
            type="submit"
            data-test="confirm-button">
            Confirm email
          </Button>
        </Buttons>
      </Row>
    </form>
  );
}

type OptionsMenuProps = {
  onResendCodeClick: () => void;
  onChangeEmailClick: () => void;
};

function OptionsMenu(props: OptionsMenuProps) {
  const textWithIconProps: Partial<TextWithIconProps> = {
    iconSize: 'inherit',
    iconColor: 'chalkboard',
    color: 'chalkboard',
  };

  const { onResendCodeClick: onResendCode, onChangeEmailClick } = props;
  return (
    <LayerTrigger
      placement="right"
      trigger="click"
      layer={
        <Menu>
          <MenuItem data-test="confirm-email-form-resend-code" onClick={onResendCode} menuKey="resend">
            <TextWithIcon icon="rotate" {...textWithIconProps}>
              Resend confirmation email
            </TextWithIcon>
          </MenuItem>
          <MenuItem data-test="confirm-email-change-email" onClick={onChangeEmailClick} menuKey="change">
            <TextWithIcon icon="pencil-stroke" {...textWithIconProps}>
              Change email
            </TextWithIcon>
          </MenuItem>
          <MenuItem
            data-test="confirm-email-contact-support"
            onClick={() => window.open(CoachingConstants.TUTORING_HELP_FORM_ROUTE, '_blank')}
            menuKey="contact">
            <TextWithIcon icon="question-circle" {...textWithIconProps}>
              Contact support
            </TextWithIcon>
          </MenuItem>
        </Menu>
      }>
      <Link>See other options</Link>
    </LayerTrigger>
  );
}

export default ConfirmEmailForm;
